import moment from "moment"
import momentTz from "moment-timezone"

const getLocalTimeZone = () => {
  const offset = new Date().getTimezoneOffset(),
    o = Math.abs(offset)
  return (
    (offset < 0 ? "+" : "-") +
    ("00" + Math.floor(o / 60)).slice(-2) +
    ":" +
    ("00" + (o % 60)).slice(-2)
  )
}

const getLocalTimeZoneName = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

const getUnixEpochTime = (momentObject) => {
  return momentObject ? momentObject.valueOf() : moment().valueOf()
}

const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  // const formattedHours = hours.toString().padStart(2, "0")
  const formattedMinutes = minutes.toString().padStart(2, "0")
  const formattedSeconds = remainingSeconds.toString().padStart(2, "0")

  if (hours === 0 && minutes === 0) {
    return `${formattedSeconds} s`
  } else if (hours === 0) {
    return `${formattedMinutes} m: ${formattedSeconds} s`
  } else {
    return `${hours} h: ${formattedMinutes} m: ${formattedSeconds} s`
  }
}

/**
 * Convert UTC date to event timezone for display in datetime-local input
 * @param {Date|string} utcDate - Date in UTC (from database)
 * @param {string} timezone - IANA timezone identifier (e.g., "UTC", "America/New_York")
 * @returns {string} Date string in format "YYYY-MM-DDTHH:mm" for datetime-local input
 */
const convertUTCToTimezoneForInput = (utcDate, timezone = "UTC") => {
  if (!utcDate) {
    return ""
  }
  
  // Parse the UTC date
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate
  
  // If timezone is UTC, format directly
  if (timezone === "UTC" || !timezone) {
    return moment.utc(date).format('YYYY-MM-DDTHH:mm')
  }
  
  // Convert UTC date to the specified timezone
  const momentDate = momentTz.utc(date).tz(timezone)
  return momentDate.format('YYYY-MM-DDTHH:mm')
}

/**
 * Convert datetime-local input value (interpreted as event timezone) to UTC ISO string
 * @param {string} dateTimeString - Date string from datetime-local input (format: "YYYY-MM-DDTHH:mm")
 * @param {string} timezone - IANA timezone identifier (e.g., "UTC", "America/New_York")
 * @returns {string} ISO string in UTC (format: "YYYY-MM-DDTHH:mm:ss.sssZ")
 */
const convertTimezoneToUTC = (dateTimeString, timezone = "UTC") => {
  if (!dateTimeString) {
    return null
  }
  
  // If timezone is UTC, parse and return as UTC
  if (timezone === "UTC" || !timezone) {
    const date = new Date(dateTimeString)
    return date.toISOString()
  }
  
  // Parse the datetime-local string and interpret it as being in the event's timezone
  // Format: "2024-01-15T10:00"
  const [datePart, timePart] = dateTimeString.split('T')
  const [year, month, day] = datePart.split('-').map(Number)
  const [hours, minutes] = (timePart || '00:00').split(':').map(Number)
  
  // Create a moment in the event's timezone, then convert to UTC
  const momentInTz = momentTz.tz(
    {
      year,
      month: month - 1, // moment months are 0-indexed
      day,
      hour: hours,
      minute: minutes
    },
    timezone
  )
  
  // Convert to UTC and return as ISO string
  return momentInTz.utc().toISOString()
}

/**
 * Format UTC date for display in the event's timezone
 * @param {Date|string} utcDate - Date in UTC (from database)
 * @param {string} timezone - IANA timezone identifier (e.g., "UTC", "America/New_York")
 * @returns {string} Formatted date string (e.g., "Jan 15, 2024 3:00 PM PST")
 */
const formatEventDateForDisplay = (utcDate, timezone = "UTC") => {
  if (!utcDate) {
    return ""
  }
  
  // Parse the UTC date
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate
  
  // If timezone is UTC, format directly as UTC
  if (timezone === "UTC" || !timezone) {
    return moment.utc(date).format('MMM D, YYYY h:mm A [UTC]')
  }
  
  // Convert UTC date to the specified timezone and format
  const momentDate = momentTz.utc(date).tz(timezone)
  
  // Get timezone abbreviation (e.g., PST, PDT, EST)
  const tzAbbr = momentDate.format('z')
  
  // Format: "Jan 15, 2024 3:00 PM PST"
  return momentDate.format('MMM D, YYYY h:mm A') + ' ' + tzAbbr
}

export { 
  getLocalTimeZone, 
  getLocalTimeZoneName, 
  getUnixEpochTime, 
  formatTime, 
  convertUTCToTimezoneForInput,
  convertTimezoneToUTC,
  formatEventDateForDisplay
}
